import { QueryClient } from '@tanstack/react-query';
import { createTRPCClient, httpBatchLink, TRPCClientError } from '@trpc/client';
import { createTRPCOptionsProxy } from '@trpc/tanstack-react-query';
import type { AppRouter } from '@studio/api/src/routers/index.js';
import { ACTIVE_BRAND_HEADER, type AccessDenied } from '@studio/contracts';
import { getActiveBrand } from './active-brand.js';
import { accessToken } from './supabase.js';

export const queryClient = new QueryClient({
  defaultOptions: { queries: { retry: (n, err) => !(err instanceof TRPCClientError) && n < 2, staleTime: 15_000, refetchOnWindowFocus: false } },
});

export const trpcClient = createTRPCClient<AppRouter>({
  links: [
    httpBatchLink({
      url: '/trpc',
      async headers() {
        const token = await accessToken();
        const brand = getActiveBrand();
        return { ...(token ? { authorization: `Bearer ${token}` } : {}), ...(brand ? { [ACTIVE_BRAND_HEADER]: brand } : {}) };
      },
    }),
  ],
});

export const trpc = createTRPCOptionsProxy<AppRouter>({ client: trpcClient, queryClient });

/** The 402 body when the api refused because the organisation's access lapsed, else null. */
export function accessDenied(err: unknown): AccessDenied | null {
  if (!(err instanceof TRPCClientError)) return null;
  const data = err.data as { access?: AccessDenied } | undefined;
  return data?.access ?? null;
}

export function isUnauthorised(err: unknown): boolean {
  return err instanceof TRPCClientError && err.data?.code === 'UNAUTHORIZED';
}

export function isForbidden(err: unknown): boolean {
  return err instanceof TRPCClientError && err.data?.code === 'FORBIDDEN';
}
